import { useLingui } from '@lingui/react/macro';
import { Chip, SxProps, Theme, Tooltip } from '@mui/material';

import useLocale from '@watershed/intl/frontend/useLocale';
import DateTimeUtils from '@watershed/shared-universal/utils/DateTimeUtils';
import mixinSx from '@watershed/style/mixinSx';
import { tooltipDarkStyleProps } from '@watershed/style/tooltips';

import Avatar from './Avatar';
import TruncatingTypographyWithTooltip from './TruncatingTypographyWithTooltip';

interface DateChipProps {
  date: Date;
  dateEventType?: DateEventType;
  userName?: string;
  isWatershedEmployee?: boolean;
  showTooltip?: boolean;
  sx?: SxProps<Theme>;
}

export type DateEventType = 'updated' | 'created' | 'submitted';

export default function DateChip({
  date,
  userName,
  sx,
  isWatershedEmployee,
  showTooltip,
  dateEventType,
}: DateChipProps) {
  const { t } = useLingui();
  const locale = useLocale();

  const formattedDate = DateTimeUtils.formatDate(
    DateTimeUtils.fromJSDate(date),
    { format: 'med', locale }
  );
  const formattedDateTime = DateTimeUtils.formatDateTime(
    DateTimeUtils.fromJSDate(date),
    { format: 'med', locale }
  );

  const chip = (
    <Chip
      component="time"
      dateTime={date.toISOString()}
      variant="outlined"
      // Use icon and not avatar prop otherwise styles get overwritten.
      icon={
        userName ? (
          <Avatar variant="person" name={userName} size="small" />
        ) : undefined
      }
      label={
        // This allows the label to truncate and show a ... with longer strings
        <TruncatingTypographyWithTooltip
          variant="body1"
          sx={{
            color: (theme) => theme.palette.grey50,
            fontWeight: 'bold',
            userSelect: 'none',
          }}
          tooltip={formattedDate}
        />
      }
      tabIndex={showTooltip ? 0 : undefined}
      sx={mixinSx(
        {
          borderColor: (theme) => theme.palette.grey30,
          fontSize: (theme) => theme.typography.body2.fontSize,
          borderRadius: 2,
          display: 'flex',
          gap: 0.25,
          px: 0.5,
          width: 'fit-content',
          '& .MuiChip-label': {
            py: 0,
            px: 0.25,
          },
          '& .MuiChip-icon': {
            display: 'flex',
            alignItems: 'center',
            m: 0,
            p: 0,
            color: (theme) => theme.palette.white,
          },
        },
        !showTooltip && {
          '&.MuiChip-root:hover': {
            backgroundColor: (theme) => theme.palette.paper,
          },
        },
        sx
      )}
    />
  );

  if (!showTooltip) return chip;

  const userNameWithWsEmployeeSuffix = isWatershedEmployee
    ? t({
        message: `${userName} (Watershed employee)`,
        context: 'Date chip tooltip',
      })
    : userName;

  let titleForTooltip = null;
  switch (dateEventType) {
    case 'created':
      titleForTooltip = userNameWithWsEmployeeSuffix
        ? t({
            message: `Created on ${formattedDateTime} by ${userNameWithWsEmployeeSuffix}`,
            context: 'Date chip tooltip',
          })
        : t({
            message: `Created on ${formattedDateTime}`,
            context: 'Date chip tooltip',
          });
      break;
    case 'submitted':
      titleForTooltip = userNameWithWsEmployeeSuffix
        ? t({
            message: `Submitted on ${formattedDateTime} by ${userNameWithWsEmployeeSuffix}`,
            context: 'Date chip tooltip',
          })
        : t({
            message: `Submitted on ${formattedDateTime}`,
            context: 'Date chip tooltip',
          });
      break;
    case 'updated':
    default:
      titleForTooltip = userNameWithWsEmployeeSuffix
        ? t({
            message: `Last updated on ${formattedDateTime} by ${userNameWithWsEmployeeSuffix}`,
            context: 'Date chip tooltip',
          })
        : t({
            message: `Last updated on ${formattedDateTime}`,
            context: 'Date chip tooltip',
          });
      break;
  }

  return (
    <Tooltip title={titleForTooltip} slotProps={tooltipDarkStyleProps()}>
      {chip}
    </Tooltip>
  );
}
